import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Paper, Grid, Button, TextField, InputAdornment, Chip, IconButton, CircularProgress } from '@material-ui/core';
import { ExpandMore, Search, Close, ThumbUpAltOutlined, ThumbUpAlt, ThumbDownAltOutlined, ThumbDownAlt, UnfoldMore, UnfoldLess, HelpOutline, HomeOutlined, GroupOutlined, TuneOutlined, SettingsOutlined, DescriptionOutlined, BuildOutlined, ForumOutlined, FolderOutlined, AssignmentOutlined, FlightOutlined, WhatshotRounded, RemoveRedEyeOutlined } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { checkApiStatus } from '../../utils_v2';
import { globalGetService, globalPostService } from '../../../globalServices';
import { PageLoader } from '../../shared_elements';

const CATEGORY_ICONS = {
    'Console': TuneOutlined,
    'Home Page': HomeOutlined,
    'Assets': FolderOutlined,
    'User Management': GroupOutlined,
    'Settings': SettingsOutlined,
    'Lease Management': DescriptionOutlined,
    'Maintenance Management': BuildOutlined,
    'CommVerge': ForumOutlined,
    'Records Dataroom': FolderOutlined,
    'Projects Management': AssignmentOutlined,
    'Airworthiness Management': FlightOutlined
};

const COLORS = {
    primary: '#6252c7',
    primarySoft: '#EEEAFF',
    accent: '#16866f',
    accentSoft: '#E6F7F3',
    warn: '#d37a0d',
    warnSoft: '#FFF3DF',
    danger: '#DF5360',
    dangerSoft: '#FEECEE',
    textMuted: '#6d7187',
    border: '#E7E8F0',
    pageBg: '#F6F7FB'
};

const ACCENT_ROTATION = ['#6252c7', '#dd6b20', '#087ea4', '#c44f76', '#1676a5', '#db8c12', '#16866f', '#70843b'];

const CATEGORY_QUERY_MAP = {
    'Console': 'console',
    'Lease Management': 'lease-management',
    'Maintenance Management': 'maintenance-management',
    'CommVerge': 'commverge',
    'Records Dataroom': 'records-dataroom',
    'Projects Management': 'projects-management',
    'Airworthiness Management': 'airworthiness-management',
    'fin-S':'fin-s'
};
const MODULE_WISE_QUERY = {
    1: "Console",
    2: "Maintenance Claim Management",
    3: "Lease Management",
    4: "CommVerge",
    6: "Records Dataroom",
    8: "Projects Management",
    14: "fin-S",
    18: "Airworthiness Management", 
    33: "Lease Management"
}

const getCategoryIcon = (section) => CATEGORY_ICONS[section] || HelpOutline;
const getCategoryAccent = (section, categories) => ACCENT_ROTATION[Math.max(categories.indexOf(section), 0) % ACCENT_ROTATION.length];

const toText = (value) => (value == null ? '' : String(value));
const isHtml = (str) => /<\/?[a-z][\s\S]*>/i.test(toText(str));
const stripHtml = (str) => toText(str).replace(/<[^>]*>/g, ' ');
const faqKey = (section, faq, index) => `${section}::${faq?.id ?? `idx-${index}`}`;
const matchesTerm = (faq, term) => (
    !term ||
    toText(faq?.question).toLowerCase().includes(term) ||
    stripHtml(faq?.answer).toLowerCase().includes(term)
);

// Keep only array categories with object FAQs so a malformed response can't crash the page
const normalizeCategories = (categories) => {
    if (!categories || typeof categories !== 'object' || Array.isArray(categories)) return {};
    return Object.entries(categories).reduce((acc, [section, faqs]) => {
        if (Array.isArray(faqs)) acc[section] = faqs.filter((faq) => faq && typeof faq === 'object');
        return acc;
    }, {});
};
const pluralize = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightMatch = (value, term) => {
    const text = toText(value);
    const trimmed = toText(term).trim();
    if (!trimmed) return text;
    const regex = new RegExp(`(${escapeRegExp(trimmed)})`, 'gi');
    return text.split(regex).map((part, i) => (
        i % 2 === 1
            ? <mark key={i} style={{ background: COLORS.primarySoft, color: COLORS.primary, padding: 0 }}>{part}</mark>
            : part
    ));
};

// Wraps matches inside text nodes only, so tags/attributes in the HTML answer stay intact
const highlightHtml = (html, term) => {
    const trimmed = toText(term).trim();
    if (!trimmed || !html) return toText(html);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const regex = new RegExp(`(${escapeRegExp(trimmed)})`, 'gi');
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);
    textNodes.forEach((node) => {
        const parts = node.nodeValue.split(regex);
        if (parts.length === 1) return;
        const fragment = doc.createDocumentFragment();
        parts.forEach((part, i) => {
            if (!part) return;
            if (i % 2 === 1) {
                const mark = doc.createElement('mark');
                mark.style.background = COLORS.primarySoft;
                mark.style.color = COLORS.primary;
                mark.style.padding = '0';
                mark.textContent = part;
                fragment.appendChild(mark);
            } else {
                fragment.appendChild(doc.createTextNode(part));
            }
        });
        node.parentNode.replaceChild(fragment, node);
    });
    return doc.body.innerHTML;
};

const getCategoryFromUrl = () => {
    const query = new URLSearchParams(window.location?.search || '').get('application');
    if (!query) return 'All';
    if (MODULE_WISE_QUERY[query]) return MODULE_WISE_QUERY[query];
    const match = Object.entries(CATEGORY_QUERY_MAP).find(([, slug]) => slug === query);
    return match ? match[0] : 'All';
};

export default function FAQs() {
    const [faqnewData, setFaqnewData] = useState({});
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [expanding, setExpanding] = useState(false);
    const [expandBusy, setExpandBusy] = useState(false);
    const expandLock = useRef(false);
    const [selectedCategory, setSelectedCategory] = useState(getCategoryFromUrl);
    const [expandedSections, setExpandedSections] = useState([]);
    const [expandedFaqs, setExpandedFaqs] = useState([]);
    const [feedback, setFeedback] = useState({});

    useEffect(() => {
        const timer = setTimeout(() => setSearchTerm(searchInput), 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const searching = searchInput !== searchTerm;
    const applySearch = (value) => {
        setSearchInput(value);
        setSearchTerm(value);
    };

    useEffect(() => {
        setLoading(true);
        globalGetService('console/faqs/')
            .then((response) => {
                if (checkApiStatus(response)) {
                    const responseCategories = normalizeCategories(response?.data?.data?.categories);
                    const initialFeedback = {};
                    Object.values(responseCategories).forEach((faqs) => {
                        faqs.forEach((faq) => {
                            if (faq?.id != null) initialFeedback[faq.id] = faq?.user_vote || null;
                        });
                    });
                    setFaqnewData(responseCategories);
                    setStats(response?.data?.data?.stats || {});
                    setFeedback(initialFeedback);
                }
            })
            .catch(() => {})
            .then(() => setLoading(false));
    }, []);

    const categories = useMemo(() => Object.keys(faqnewData), [faqnewData]);
    const activeCategory = selectedCategory !== 'All' && !categories.includes(selectedCategory) ? 'All' : selectedCategory;

    const filteredData = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return Object.entries(faqnewData).reduce((acc, [section, faqs]) => {
            if (activeCategory !== 'All' && section !== activeCategory) return acc;
            const sorted = [...(faqs || [])].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
            const matched = sorted.filter((faq) => matchesTerm(faq, term));
            if (matched.length) acc[section] = matched;
            return acc;
        }, {});
    }, [faqnewData, searchTerm, activeCategory]);

    const activeCounts = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return Object.fromEntries(categories.map((section) => [
            section,
            (faqnewData[section] || []).filter((faq) => matchesTerm(faq, term)).length
        ]));
    }, [faqnewData, categories, searchTerm]);

    const totalFaqsAll = useMemo(
        () => Object.values(activeCounts).reduce((sum, count) => sum + count, 0),
        [activeCounts]
    );

    const totalCategoriesAll = categories.length;

    const popularTopics = Array.isArray(stats?.most_viewed_questions) ? stats.most_viewed_questions.filter(Boolean) : [];

    const helpfulVotes = useMemo(
        () => Object.values(feedback).filter((v) => v === 'up').length,
        [feedback]
    );

    const filteredTotal = useMemo(
        () => Object.values(filteredData).reduce((sum, faqs) => sum + faqs.length, 0),
        [filteredData]
    );

    const filteredCategoriesCount = Object.keys(filteredData).length;

    const lastUpdated = useMemo(
        () => new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        []
    );

    const allKeys = useMemo(() => {
        const sectionKeys = Object.keys(filteredData);
        const faqKeys = Object.entries(filteredData).flatMap(([section, faqs]) => (
            faqs.map((faq, index) => faqKey(section, faq, index))
        ));
        return { sectionKeys, faqKeys };
    }, [filteredData]);

    const allExpanded = allKeys.sectionKeys.length > 0 &&
        (activeCategory !== 'All' || allKeys.sectionKeys.every((s) => expandedSections.includes(s))) &&
        allKeys.faqKeys.every((k) => expandedFaqs.includes(k));

    const toggleSection = (section, isExpanded) => {
        setExpandedSections((prev) => (
            isExpanded ? [...prev, section] : prev.filter((s) => s !== section)
        ));
    };

    const toggleFaq = (key, faqId, isExpanded) => {
        setExpandedFaqs((prev) => (
            isExpanded ? [...prev, key] : prev.filter((k) => k !== key)
        ));
        if (isExpanded && faqId != null) {
            globalPostService(`console/faqs/${faqId}/view/`);
        }
    };

    const handleToggleExpandAll = () => {
        if (expandLock.current) return;
        expandLock.current = true;
        setExpandBusy(true);
        setExpanding(allExpanded ? 'Collapsing...' : 'Expanding...');
        setTimeout(() => {
            if (allExpanded) {
                setExpandedSections([]);
                setExpandedFaqs([]);
            } else {
                setExpandedSections(allKeys.sectionKeys);
                setExpandedFaqs(allKeys.faqKeys);
            }
            setExpanding(false);
            setTimeout(() => {
                expandLock.current = false;
                setExpandBusy(false);
            }, 300);
        }, 50);
    };

    const renderFaqList = (section, faqs) => (
        (faqs || []).map((faq, index) => {
            const key = faqKey(section, faq, index);
            const isOpen = expandedFaqs.includes(key);

            return (
                <Accordion
                    key={key}
                    elevation={0}
                    square
                    disableGutters
                    expanded={isOpen}
                    onChange={(e, nextExpanded) => toggleFaq(key, faq?.id, nextExpanded)}
                    className={`faq-sub-accordion${isOpen ? ' is-open' : ''}`}
                >
                    <AccordionSummary expandIcon={<ExpandMore />} className='faq-sub-accordion__summary'>
                        <div className='faq-sub-accordion__summary-inner'>
                            <Typography variant='body2' className='faq-question-text'>
                                {highlightMatch(faq?.question || 'Untitled question', searchTerm)}
                            </Typography>
                        </div>
                    </AccordionSummary>
                    <AccordionDetails className='faq-sub-accordion__body'>
                        {isHtml(faq?.answer)
                            ? <div style={{ fontSize: "14px", lineHeight: 1.6, color: "#374151", }} dangerouslySetInnerHTML={{ __html: highlightHtml(faq?.answer, searchTerm) }} />
                            : <Typography variant='body2'>{faq?.answer ? highlightMatch(faq.answer, searchTerm) : 'No answer available yet.'}</Typography>}

                        {faq?.id != null && <div className='faq-help-row'>
                            <Typography variant='body2' className='faq-help-text'>Was this helpful?</Typography>
                            <IconButton size='small' onClick={() => handleFeedback(faq?.id, 'up')}>
                                {feedback[faq?.id] === 'up'
                                    ? <ThumbUpAlt fontSize='small' style={{ color: COLORS.primary }} />
                                    : <ThumbUpAltOutlined fontSize='small' />}
                            </IconButton>
                            <IconButton size='small' onClick={() => handleFeedback(faq?.id, 'down')}>
                                {feedback[faq?.id] === 'down'
                                    ? <ThumbDownAlt fontSize='small' style={{ color: COLORS.danger }} />
                                    : <ThumbDownAltOutlined fontSize='small' />}
                            </IconButton>
                            {feedback[faq?.id] && (
                                <Typography variant='caption' style={{ color: COLORS.textMuted }}>
                                    Thanks for your feedback!
                                </Typography>
                            )}
                        </div>}
                    </AccordionDetails>
                </Accordion>
            );
        })
    );

    const handleTopicClick = (name) => {
        if (!name) return;
        setSelectedCategory('All');
        applySearch(toText(name));
    };

    const handleFeedback = (faqId, type) => {
        if (faqId == null) return;
        const nextValue = feedback[faqId] === type ? null : type;
        setFeedback((prev) => ({ ...prev, [faqId]: nextValue }));
        globalPostService(`console/faqs/${faqId}/feedback/`, { value: nextValue });
    };

    return (
        <section className='faq-page-container'>
            { loading ? <PageLoader /> : null }
            <div className='faq-body'>
                <div className='faq-hero'>
                    <div>
                        <Typography variant='h5' className='faq-hero__title'>Frequently Asked Questions</Typography>
                        <Typography variant='body2' className='faq-hero__subtitle'>Find quick answers and guidance across SPARTA modules.</Typography>
                    </div>
                    <div className='faq-hero__meta'>
                        <span className='faq-hero__pill'> Updated {lastUpdated} </span>
                    </div>
                </div>
                <div className='faq-scroll-area'>
                <Grid container spacing={2} alignItems='stretch' className='faq-columns'>
                    <Grid item md={9} xs={12} className='faq-column faq-column--main'>
                        <Paper className='faq-filter-card'>
                            <div className='faq-filter-row'>
                                <div className='faq-search-wrap'>
                                    <TextField
                                        placeholder='Search questions, answers or keywords...'
                                        fullWidth
                                        size='small'
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        variant='outlined'
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position='start'>
                                                    <Search fontSize='small' />
                                                </InputAdornment>
                                            ),
                                            endAdornment: searchInput ? (
                                                <InputAdornment position='end'>
                                                    {searching && <CircularProgress size={16} style={{ color: COLORS.primary, marginRight: 4 }} />}
                                                    <IconButton size='small' onClick={() => applySearch('')}>
                                                        <Close fontSize='small' />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : undefined
                                        }}
                                    />
                                </div>
                                <Button
                                    variant='outlined'
                                    color='inherit'
                                    startIcon={allExpanded ? <UnfoldLess fontSize='small' /> : <UnfoldMore fontSize='small' />}
                                    className='faq-expand-btn'
                                    onClick={handleToggleExpandAll}
                                    disabled={allKeys.sectionKeys.length === 0 || expandBusy || searching}
                                >
                                    {allExpanded ? 'Collapse All' : 'Expand All'}
                                </Button>
                            </div>

                            <div className='faq-category-row'>
                                <Chip
                                    disableRipple
                                    onClick={() => setSelectedCategory('All')}
                                    className={activeCategory === 'All' ? 'faq-chip is-active' : 'faq-chip'}
                                    label={
                                        <span className='faq-chip__label'>
                                            <span>All</span>
                                            <span className={activeCategory === 'All' ? 'faq-chip__count is-active' : 'faq-chip__count'}>{totalFaqsAll}</span>
                                        </span>
                                    }
                                />
                                {categories.map((section) => {
                                    const SectionIcon = getCategoryIcon(section);
                                    const active = activeCategory === section;
                                    if (!activeCounts[section] && !active) return null;
                                    return (
                                        <Chip
                                            key={section}
                                            disableRipple
                                            onClick={() => setSelectedCategory(section)}
                                            className={active ? 'faq-chip is-active' : 'faq-chip'}
                                            label={
                                                <span className='faq-chip__label'>
                                                    <span>{section || 'Other'}</span>
                                                    <span className={active ? 'faq-chip__count is-active' : 'faq-chip__count'}>{activeCounts[section] || 0}</span>
                                                </span>
                                            }
                                        />
                                    );
                                })}
                            </div>
                        </Paper>

                        <Paper className='faq-section-panel'>
                            <div className='faq-section-panel__header'>
                                <Typography variant='body1' className='faq-section-panel__title'>
                                    {pluralize(filteredTotal, 'FAQ', 'FAQs')} across {pluralize(filteredCategoriesCount, 'category', 'categories')}
                                </Typography>
                            </div>

                            <div className='faq-section-panel__body'>
                                {!loading && (searching || expanding) && (
                                    <div className='faq-empty-state' style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <CircularProgress size={24} style={{ color: COLORS.primary, marginBottom: 8 }} />
                                        <Typography variant='body2'>{searching ? 'Searching...' : expanding}</Typography>
                                    </div>
                                )}
                                {!loading && !searching && !expanding && Object.keys(filteredData).length === 0 && (
                                    <div className='faq-empty-state'>
                                        <div className='faq-empty-state__card'>
                                            <div className='faq-empty-state__icon'>
                                                <Search style={{ fontSize: 28 }} />
                                            </div>
                                            <Typography variant='h6' className='faq-section-title'> No matching FAQs </Typography>
                                            <Typography variant='body2' className='faq-empty-state__text'>
                                                Try a different keyword or clear the current filters to see more questions.
                                            </Typography>
                                            {(searchTerm || activeCategory !== 'All') && (
                                                <div className='faq-empty-state__actions'>
                                                    <Button
                                                        size='small'
                                                        variant='contained'
                                                        className='faq-empty-state__button'
                                                        onClick={() => {
                                                            applySearch('');
                                                            setSelectedCategory('All');
                                                        }}
                                                    >
                                                        Clear filters
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* A single selected module lists its questions directly, without the module accordion */}
                                {!searching && !expanding && activeCategory !== 'All' && Object.entries(filteredData).map(([section, faqs]) => (
                                    <div key={section} className='faq-sub-accordion__details'>
                                        {renderFaqList(section, faqs)}
                                    </div>
                                ))}
                                {!searching && !expanding && activeCategory === 'All' && Object.entries(filteredData).map(([section, faqs]) => {
                                    const SectionIcon = getCategoryIcon(section);
                                    const accent = getCategoryAccent(section, categories);

                                    return (
                                        <Accordion
                                            key={section}
                                            elevation={0}
                                            square
                                            disableGutters
                                            expanded={expandedSections.includes(section)}
                                            onChange={(e, isExp) => toggleSection(section, isExp)}
                                            className='faq-accordion'
                                            style={{ '--faq-accent': accent }}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMore />} className='faq-accordion__summary'>
                                                <div className='faq-accordion__summary-inner'>
                                                    <SectionIcon fontSize='small' className='faq-accent-icon' />
                                                    <Typography variant='body1' className='faq-section-text'>
                                                        {section || 'Other'}
                                                    </Typography>
                                                    <Chip
                                                        label={pluralize(faqs?.length || 0, 'FAQ', 'FAQs')}
                                                        size='small'
                                                        className='faq-faq-chip'
                                                    />
                                                </div>
                                            </AccordionSummary>

                                            <AccordionDetails className='faq-sub-accordion__details'>
                                                {renderFaqList(section, faqs)}
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                            </div>
                        </Paper>
                    </Grid>

                    <Grid item md={3} xs={12} className='faq-column'>
                        <div className='faq-sidebar'>
                            <Paper className='faq-sidebar-card'>
                                <Typography variant='body1' className='faq-section-title'>
                                    FAQs Overview
                                </Typography>

                                <div className='faq-overview-stack'>
                                    <div className='faq-overview-row' style={{ background: COLORS.primarySoft }}>
                                        <Typography variant='body2' style={{ color: COLORS.textMuted }}>Categories</Typography>
                                        <Typography variant='body1' className='faq-overview-value' style={{ color: COLORS.primary }}>{totalCategoriesAll}</Typography>
                                    </div>
                                    <div className='faq-overview-row' style={{ background: COLORS.accentSoft }}>
                                        <Typography variant='body2' style={{ color: COLORS.textMuted }}>Most viewed category</Typography>
                                        <Typography variant='body2' className='faq-overview-value' style={{ color: COLORS.accent }}>{stats?.most_viewed_category || '—'}</Typography>
                                    </div>
                                    <div className='faq-overview-row' style={{ background: COLORS.warnSoft, gap: 8 }}>
                                        <Typography variant='body2' style={{ color: COLORS.textMuted, whiteSpace: 'nowrap' }}>Most liked category</Typography>
                                        <Typography variant='body2' className='faq-overview-value' style={{ color: COLORS.warn, textAlign: 'right' }} noWrap>
                                            {stats?.most_liked_category || '—'}
                                        </Typography>
                                    </div>
                                    <div className='faq-overview-row' style={{ background: COLORS.dangerSoft }}>
                                        <Typography variant='body2' style={{ color: COLORS.textMuted }}>Marked helpful</Typography>
                                        <Typography variant='body1' className='faq-overview-value' style={{ color: COLORS.danger }}>{helpfulVotes}</Typography>
                                    </div>
                                </div>
                            </Paper>

                            <Paper elevation={0} className='faq-popular-card'>
                                <div className='faq-popular-card__header'>
                                    <div className='faq-popular-card__heading'>
                                        <div className='faq-popular-card__icon'> <WhatshotRounded /> </div>
                                        <div>
                                            <Typography className='faq-popular-card__title'>Popular topics</Typography>
                                            <Typography className='faq-popular-card__subtitle'>Most viewed articles</Typography>
                                        </div>
                                    </div>
                                </div>
                                <div className='faq-popular-card__divider' />
                                <div>
                                    {loading && [...Array(5)].map((_, index) => (
                                        <div key={index} className='faq-popular-row' style={{ cursor: 'default', gap: 10 }}>
                                            <Skeleton variant='rect' width={32} height={32} style={{ borderRadius: 8, flexShrink: 0 }} />
                                            <div style={{ flex: 1 }}>
                                                <Skeleton variant='text' width='90%' />
                                                <Skeleton variant='text' width='60%' />
                                            </div>
                                            <Skeleton variant='text' width={50} />
                                        </div>
                                    ))}
                                    {!loading && popularTopics.map((item, index) => (
                                        <div
                                            key={item?.id ?? index}
                                            onClick={() => handleTopicClick(item?.question)}
                                            className='faq-popular-row'
                                        >
                                            <div className='faq-popular-row__number'> {(index + 1).toString().padStart(2, '0')} </div>
                                            <Typography className='faq-popular-row__title'>{item?.question || 'Untitled question'}</Typography>
                                            <div className='faq-popular-row__views'>
                                                <RemoveRedEyeOutlined fontSize='small' />
                                                <Typography className='faq-popular-row__views-text'>{pluralize(Number(item?.views) || 0, 'view', 'views')}</Typography>
                                            </div>
                                        </div>
                                    ))}
                                    {!loading && popularTopics.length === 0 && (
                                        <Typography variant='body2' style={{ color: COLORS.textMuted, padding: '12px 8px' }}>
                                            No popular topics yet.
                                        </Typography>
                                    )}
                                </div>
                            </Paper>

                            <Paper className='faq-sidebar-card faq-sidebar-card--support'>
                                <div className='faq-sidebar__support-header'>
                                    <HelpOutline fontSize='small' className='faq-sidebar__support-icon' />
                                    <Typography variant='body1' className='faq-section-title'>Still need help?</Typography>
                                </div>
                                <div className='faq-sidebar__support-body'>
                                    <Typography variant='body2' className='faq-sidebar__support-copy'>Our support team is here to help with any SPARTA question.</Typography>
                                    <div style={{ height: 10 }} />
                                    <Button variant='contained' className='faq-sidebar__support-button' fullWidth component='a' href='mailto:support@sparta.aero?subject=SPARTA%20Support%20Request' >Contact Support</Button>
                                    <div style={{ height: 10 }} />
                                    <Typography variant='body2' align='center' className='faq-sidebar__support-email'>support@sparta.aero</Typography>
                                </div>
                            </Paper>
                        </div>
                    </Grid>
                </Grid>
                </div>
            </div>
        </section>
    );
}
